/**
 * Permissions utility functions
 * 
 * This file contains frontend logic to compute effective permissions
 * based on role and permissions_override.
 */

export type PermissionOverride = {
  allow?: string[];
  deny?: string[];
};

export type PermissionState = "default" | "allow" | "deny";

/**
 * Available permissions in the system
 * These should match the backend permissions matrix
 */
export const AVAILABLE_PERMISSIONS = [
  { id: "read", label: "Lecture", description: "Consulter les informations", category: "Général" },
  { id: "manage_users", label: "Gérer les utilisateurs", description: "Créer, modifier et supprimer des utilisateurs", category: "Utilisateurs" },
  { id: "manage_memberships", label: "Gérer les membreships", description: "Gérer les accès et membreships des utilisateurs", category: "Utilisateurs" },
  { id: "manage_ambassadors", label: "Gérer les ambassadeurs", description: "Gérer les ambassadeurs et leurs relations", category: "Ambassadeurs" },
] as const;

/**
 * Role descriptions for user-friendly display (canonical 4 roles + legacy fallback).
 */
export const ROLE_DESCRIPTIONS: Record<string, { label: string; description: string; capabilities: string[] }> = {
  superadmin: {
    label: "Super Administrateur",
    description: "Accès complet à toutes les fonctionnalités du système",
    capabilities: [
      "Consulter toutes les informations",
      "Gérer tous les utilisateurs",
      "Gérer les accès et membreships",
      "Gérer les ambassadeurs",
      "Accès à toutes les fonctionnalités administratives",
    ],
  },
  owner: {
    label: "Propriétaire",
    description: "Contrôle total sur l'organisation",
    capabilities: [
      "Toutes les permissions de l'organisation",
      "Gérer les utilisateurs et rôles",
      "Gérer les ambassadeurs et l'organisation",
    ],
  },
  admin: {
    label: "Administrateur",
    description: "Gestion complète de l'organisation",
    capabilities: [
      "Gérer les utilisateurs et rôles",
      "Gérer les ambassadeurs",
      "Accès étendu (sauf finance.cost.reverse selon org)",
    ],
  },
  sales: {
    label: "Ventes",
    description: "Relation client et ventes",
    capabilities: [
      "Clients et ventes (lecture/écriture)",
      "Catalogue (lecture)",
      "Visibilité ops limitée",
    ],
  },
  ops: {
    label: "Opérations",
    description: "Exécution opérationnelle",
    capabilities: [
      "Engagements et process cases (lecture/écriture)",
      "Compléter les activités",
      "Coûts et document opérationnel selon org",
    ],
  },
  // Legacy (fallback if API still returns old values)
  org_owner: { label: "Propriétaire", description: "Contrôle total", capabilities: [] },
  org_admin: { label: "Administrateur", description: "Gestion complète", capabilities: [] },
  operator: { label: "Opérateur", description: "Exécution", capabilities: [] },
  sales_rep: { label: "Ventes", description: "Relation client", capabilities: [] },
  analyst: { label: "Analyste", description: "Lecture", capabilities: [] },
  readonly: { label: "Lecture seule", description: "Consultation", capabilities: [] },
};

/**
 * Default permissions by role (canonical keys for UI; backend is source of truth).
 */
export const DEFAULT_PERMISSIONS_BY_ROLE: Record<string, string[]> = {
  superadmin: ["read", "manage_users", "manage_memberships", "manage_ambassadors"],
  owner: ["read", "manage_users", "manage_memberships", "manage_ambassadors"],
  admin: ["read", "manage_users", "manage_memberships", "manage_ambassadors"],
  sales: ["read", "manage_ambassadors"],
  ops: ["read", "manage_ambassadors"],
  org_owner: ["read", "manage_users", "manage_memberships", "manage_ambassadors"],
  org_admin: ["read", "manage_users", "manage_memberships", "manage_ambassadors"],
  operator: ["read", "manage_ambassadors"],
  sales_rep: ["read", "manage_ambassadors"],
  analyst: ["read"],
  readonly: ["read"],
};

/**
 * Get default permissions for a role
 */
export function getDefaultPermissions(role: string): string[] {
  return DEFAULT_PERMISSIONS_BY_ROLE[role] || ["read"];
}

/**
 * Get role description
 */
export function getRoleDescription(role: string) {
  return ROLE_DESCRIPTIONS[role] || {
    label: role,
    description: "Rôle personnalisé",
    capabilities: [],
  };
}

/**
 * Get permission state for a specific permission
 * Returns: "default" | "allow" | "deny"
 */
export function getPermissionState(
  permission: string,
  role: string,
  override: PermissionOverride | null | undefined
): PermissionState {
  const defaultPerms = getDefaultPermissions(role);
  const hasDefault = defaultPerms.includes(permission);

  if (!override) {
    return hasDefault ? "default" : "default";
  }

  // Check deny first (highest priority)
  if (override.deny?.includes(permission)) {
    return "deny";
  }

  // Check allow
  if (override.allow?.includes(permission)) {
    return "allow";
  }

  // Fall back to default
  return hasDefault ? "default" : "default";
}

/**
 * Get effective permissions (final list after applying overrides)
 */
export function getEffectivePermissions(
  role: string,
  override: PermissionOverride | null | undefined
): string[] {
  const defaultPerms = getDefaultPermissions(role);
  const allow = override?.allow || [];
  const deny = override?.deny || [];

  // Start with default permissions
  const effective = new Set(defaultPerms);

  // Remove denied permissions
  deny.forEach((perm) => effective.delete(perm));

  // Add explicitly allowed permissions
  allow.forEach((perm) => effective.add(perm));

  return Array.from(effective).sort();
}

/**
 * Get exceptions (permissions that differ from role defaults)
 */
export function getExceptions(
  role: string,
  override: PermissionOverride | null | undefined
): { added: string[]; removed: string[] } {
  if (!override) {
    return { added: [], removed: [] };
  }

  const defaultPerms = getDefaultPermissions(role);
  const allow = override.allow || [];
  const deny = override.deny || [];

  // Added: permissions in allow that are not in default
  const added = allow.filter((perm) => !defaultPerms.includes(perm));

  // Removed: permissions in deny that are in default
  const removed = deny.filter((perm) => defaultPerms.includes(perm));

  return { added, removed };
}

/**
 * Update permission override based on new state
 */
export function updatePermissionOverride(
  currentOverride: PermissionOverride | null | undefined,
  permission: string,
  action: "add" | "remove"
): PermissionOverride | null {
  const allow = new Set(currentOverride?.allow || []);
  const deny = new Set(currentOverride?.deny || []);

  if (action === "add") {
    // Add to allow, remove from deny
    allow.add(permission);
    deny.delete(permission);
  } else {
    // Add to deny, remove from allow
    deny.add(permission);
    allow.delete(permission);
  }

  // Return null if both lists are empty (no exceptions)
  if (allow.size === 0 && deny.size === 0) {
    return null;
  }

  return {
    allow: allow.size > 0 ? Array.from(allow).sort() : undefined,
    deny: deny.size > 0 ? Array.from(deny).sort() : undefined,
  };
}

/**
 * Get permission summary text for display
 */
export function getPermissionSummary(
  role: string,
  override: PermissionOverride | null | undefined
): string {
  const defaultPerms = getDefaultPermissions(role);
  const allowCount = override?.allow?.length || 0;
  const denyCount = override?.deny?.length || 0;

  if (allowCount === 0 && denyCount === 0) {
    return `Défaut: ${defaultPerms.length} droits`;
  }

  const parts: string[] = [];
  parts.push(`Défaut: ${defaultPerms.length}`);
  if (allowCount > 0) {
    parts.push(`+${allowCount}`);
  }
  if (denyCount > 0) {
    parts.push(`-${denyCount}`);
  }

  return parts.join(" • ");
}
